// apps/prompts/src/components/OnboardingOverlay.tsx
import { Button, AppIcon, IconArrowLeft, IconArrowRight, IconCheck } from '@neuroforge/ui'
import { motion, AnimatePresence } from 'framer-motion'
import { useEffect, useState, useRef } from 'react'

import { useOnboarding, type OnboardingStep } from '../hooks/useOnboarding'

function getTargetRect(selector: string): DOMRect | null {
  const el = document.querySelector(selector)
  return el ? el.getBoundingClientRect() : null
}

function SpotlightTooltip({
  step,
  stepIndex,
  totalSteps,
  onNext,
  onPrevious,
  onSkip,
}: {
  step: OnboardingStep
  stepIndex: number
  totalSteps: number
  onNext: () => void
  onPrevious: () => void
  onSkip: () => void
}) {
  const [rect, setRect] = useState<DOMRect | null>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const updateRect = () => setRect(getTargetRect(step.target))
    updateRect()

    // Recalculate on scroll/resize
    window.addEventListener('resize', updateRect)
    window.addEventListener('scroll', updateRect, true)
    return () => {
      window.removeEventListener('resize', updateRect)
      window.removeEventListener('scroll', updateRect, true)
    }
  }, [step.target])

  if (!rect) return null

  // Position tooltip relative to target
  const padding = 12
  const tooltipStyle: React.CSSProperties = {}
  switch (step.position) {
    case 'bottom':
      tooltipStyle.top = rect.bottom + padding
      tooltipStyle.left = rect.left + rect.width / 2
      tooltipStyle.transform = 'translateX(-50%)'
      break
    case 'top':
      tooltipStyle.bottom = window.innerHeight - rect.top + padding
      tooltipStyle.left = rect.left + rect.width / 2
      tooltipStyle.transform = 'translateX(-50%)'
      break
    case 'right':
      tooltipStyle.top = rect.top + rect.height / 2
      tooltipStyle.left = rect.right + padding
      tooltipStyle.transform = 'translateY(-50%)'
      break
    case 'left':
      tooltipStyle.top = rect.top + rect.height / 2
      tooltipStyle.right = window.innerWidth - rect.left + padding
      tooltipStyle.transform = 'translateY(-50%)'
      break
  }

  return (
    <>
      {/* Spotlight cutout overlay */}
      <div
        className="fixed inset-0 z-[998]"
        style={Object.assign(
          {},
          {
            background: `radial-gradient(ellipse ${rect.width + 40}px ${rect.height + 40}px at ${rect.left + rect.width / 2}px ${rect.top + rect.height / 2}px, transparent 0%, rgba(0,0,0,0.65) 100%)`,
          },
        )}
        onClick={onSkip}
      />

      {/* Tooltip */}
      <motion.div
        ref={tooltipRef}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="border-(--border) fixed z-[999] w-72 rounded-2xl border bg-white p-5 shadow-2xl dark:bg-slate-900"
        style={tooltipStyle}
      >
        {/* Step indicator */}
        <div className="mb-3 flex items-center justify-between">
          <div className="flex gap-1">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div
                key={i}
                className={`h-2 w-2 rounded-full transition-colors ${
                  i === stepIndex ? 'bg-indigo-500' : 'bg-slate-300 dark:bg-slate-600'
                }`}
              />
            ))}
          </div>
          <button
            onClick={onSkip}
            className="text-[10px] font-bold text-slate-400 hover:text-slate-600"
          >
            Passer
          </button>
        </div>

        <h3 className="mb-1.5 text-sm font-extrabold tracking-tight">{step.title}</h3>
        <p className="mb-4 text-xs leading-relaxed text-slate-500">{step.description}</p>

        <div className="flex justify-between">
          {stepIndex > 0 ? (
            <Button variant="ghost" size="sm" onClick={onPrevious}>
              <span className="inline-flex items-center gap-1.5">
                <AppIcon icon={IconArrowLeft} size={16} aria-hidden /> Précédent
              </span>
            </Button>
          ) : (
            <div />
          )}
          <Button size="sm" onClick={onNext}>
            {stepIndex === totalSteps - 1 ? (
              <span className="inline-flex items-center gap-1.5">
                Terminer <AppIcon icon={IconCheck} size={16} aria-hidden />
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5">
                Suivant <AppIcon icon={IconArrowRight} size={16} aria-hidden />
              </span>
            )}
          </Button>
        </div>
      </motion.div>
    </>
  )
}

export default function OnboardingOverlay() {
  const { isActive, step, currentStep, totalSteps, next, previous, skip } = useOnboarding()

  return (
    <AnimatePresence>
      {isActive && step && (
        <SpotlightTooltip
          step={step}
          stepIndex={currentStep}
          totalSteps={totalSteps}
          onNext={next}
          onPrevious={previous}
          onSkip={skip}
        />
      )}
    </AnimatePresence>
  )
}
